import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { DataTag } from '@/components/DataTag/DataTag'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { toaster } from '@/components/ui/toaster'
import {
  type PlatformMigrationCohort,
  derivePlatformMigration,
} from '@/features/AccountManagement/lib/platformMigration'
import { hivesOverviewOrganizationDetailsRoute } from '@/features/HivesOverview/pages/HivesOverviewOrganizationDetails'
import { OrgDisambiguationCards } from '@/features/Licenses/components/OrgDisambiguationCard'
import { useCanManageLicenses } from '@/features/Licenses/hooks/useCanManageLicenses'
import {
  useActivateUser,
  useResendActivationEmail,
} from '@/generated/http-clients/v2ApiComponents'
import type { UserDetail } from '@/generated/http-clients/v3ApiSchemas'
import {
  ERROR_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
} from '@/hooks/useToast/toastUtils'
import {
  Badge,
  Box,
  Button,
  DataList,
  Grid,
  GridItem,
  HStack,
  Table,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { useNavigate } from '@tanstack/react-router'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

export const ACTIVATED_STATUS = 'ACTIVATED'

/**
 * The Auth0 connection backing a username/password login. Only these identities
 * can be sent an activation email — a social login has no password to set.
 */
const DATABASE_CONNECTION = 'Username-Password-Authentication'

/**
 * Friendly labels for the Auth0 connections we know about. Anything else is
 * rendered verbatim: a new connection should show up as its real name rather
 * than be silently mislabelled, which is exactly what the old user-id prefix
 * sniffing did.
 */
const CONNECTION_LABEL_KEYS: Record<string, string> = {
  [DATABASE_CONNECTION]: 'usernameAndPassword',
  'google-oauth2': 'google',
  github: 'github',
  linkedin: 'linkedin',
}

/**
 * Matches the audit timestamp format on the license detail view
 * (`MetadataPanel`) — these are moments, so they carry a clock time and a UTC
 * suffix.
 */
const TIMESTAMP_FORMAT = new Intl.DateTimeFormat('en-US', {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'UTC',
})

/**
 * Colour carries the cohort, not the current app: "never touched by the
 * rollout" is the state support most often needs to spot, so `notMigrated` is
 * the only one that reads as neutral/grey.
 */
const COHORT_COLOR_PALETTES: Record<PlatformMigrationCohort, string> = {
  registeredInPlatform: 'blue',
  migrated: 'green',
  optedIn: 'teal',
  notMigrated: 'gray',
}

export function UserAccount({ user }: Readonly<{ user: UserDetail }>) {
  const { t } = useTranslation('features', { useSuspense: false })
  const navigate = useNavigate()
  const { canManage: canManageLicenses } = useCanManageLicenses()

  const {
    mutate: activateUser,
    isPending: isActivatingUser,
    isSuccess: isUserActivatedSuccessfully,
  } = useActivateUser({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('accountManagement.account.toast.activateSuccess'),
      })
    },
    onError: () => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('accountManagement.account.toast.activateError'),
      })
    },
  })

  const {
    mutate: resendActivationEmail,
    isPending: isResendingActivationEmail,
    isSuccess: isActivationEmailResentSuccessfully,
  } = useResendActivationEmail({
    onSuccess: () => {
      toaster.create({
        ...SUCCESS_TOAST_OPTION,
        title: t('accountManagement.account.toast.resendSuccess'),
      })
    },
    onError: () => {
      toaster.create({
        ...ERROR_TOAST_OPTION,
        title: t('accountManagement.account.toast.resendError'),
      })
    },
  })

  const { cohort, usesPlatform } = derivePlatformMigration(user)
  const dash = String(t('accountManagement.account.emptyDash'))
  const utcSuffix = String(t('accountManagement.account.utcSuffix'))
  const connections = (user.identities ?? []).map((identity) =>
    getConnectionDisplayValue(identity.connection, t),
  )

  const isUserActive =
    user.activationStatus === ACTIVATED_STATUS || isUserActivatedSuccessfully

  // Only a database identity can be re-sent an activation email. Absent
  // `identities` therefore hides the button rather than guessing.
  const showResendButton =
    !isUserActive &&
    (user.identities ?? []).some(
      (identity) => identity.connection === DATABASE_CONNECTION,
    )

  const organizations = user.organizations ?? []

  return (
    <Box key={user.id} p={4}>
      <HStack justifyContent="flex-end" width="100%" mb={4}>
        <HStack>
          {!isUserActive && (
            <Button
              onClick={() =>
                activateUser({
                  body: { userId: user.id },
                })
              }
              loading={isActivatingUser}
              colorPalette="green"
              size="sm"
            >
              {t('accountManagement.account.activateButton')}
            </Button>
          )}
          {showResendButton && (
            <Button
              onClick={() =>
                resendActivationEmail({
                  body: { userId: user.id },
                })
              }
              loading={isResendingActivationEmail}
              disabled={isActivationEmailResentSuccessfully}
              colorPalette="blue"
              size="sm"
            >
              {t('accountManagement.account.resendButton')}
            </Button>
          )}
        </HStack>
      </HStack>

      <Grid templateColumns="repeat(2, 1fr)" width="100%" gap={8}>
        <GridItem>
          <SectionCard
            title={t('accountManagement.account.title')}
            width="100%"
            height="100%"
          >
            <VStack align="stretch" gap={4}>
              {/* `DataList` + `CopyableValue`, the same label/value pairing Org
                  Details and the licence cards use. Vertical orientation
                  because this card is one of two columns — too narrow for a
                  label beside an Auth0 user id. */}
              <DataList.Root orientation="vertical" gap={4}>
                {/* First in the list: this is the field support opens the page
                    for. Badge answers "how did they get here", hint answers
                    "where are they now" — the two are independent, so a
                    migrated user who switched back reads as
                    "Migrated / Uses Cloud Console" without a state of its own.
                    The tooltip carries the caveats neither has room for. */}
                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.platformMigration.label')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <HStack justifyContent="space-between" gap={3} width="100%">
                      <Tooltip.Root positioning={{ placement: 'top' }}>
                        <Tooltip.Trigger asChild>
                          <Badge
                            colorPalette={COHORT_COLOR_PALETTES[cohort]}
                            variant="subtle"
                            data-testid="platform-migration"
                          >
                            {t(
                              `accountManagement.account.platformMigration.cohort.${cohort}`,
                            )}
                          </Badge>
                        </Tooltip.Trigger>
                        <Tooltip.Positioner>
                          <Tooltip.Content maxWidth="20rem">
                            <Tooltip.Arrow />
                            {t(
                              `accountManagement.account.platformMigration.tooltip.${cohort}`,
                            )}
                          </Tooltip.Content>
                        </Tooltip.Positioner>
                      </Tooltip.Root>
                      <Text
                        fontSize="xs"
                        color="text.text-subtle"
                        textAlign="right"
                      >
                        {t(
                          usesPlatform
                            ? 'accountManagement.account.platformMigration.usesPlatform'
                            : 'accountManagement.account.platformMigration.usesConsole',
                        )}
                      </Text>
                    </HStack>
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.userId')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue value={user.id} fontFamily="mono" />
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.email')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue value={user.email} emptyText={dash} />
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.name')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue value={user.name} emptyText={dash} />
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.authMethod.label')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    {/* Linked identities are joined rather than listed: a user
                        with both a password and a Google login has one answer
                        to "how do they sign in", not two rows. */}
                    <CopyableValue
                      value={connections.join(', ')}
                      hasCopy={false}
                      emptyText={String(
                        t('accountManagement.account.authMethod.notAvailable'),
                      )}
                    />
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.activationStatus.label')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    {/* Still a `DataTag`: activation is a status, and the
                        green/red pill is the point. */}
                    <DataTag
                      value={
                        isUserActive
                          ? String(
                              t(
                                'accountManagement.account.activationStatus.activated',
                              ),
                            )
                          : String(
                              t(
                                'accountManagement.account.activationStatus.notActive',
                              ),
                            )
                      }
                      fontWeight={500}
                      hasCopy={false}
                      colorPalette={isUserActive ? 'green' : 'red'}
                    >
                      {isUserActive
                        ? t(
                            'accountManagement.account.activationStatus.activated',
                          )
                        : t(
                            'accountManagement.account.activationStatus.notActive',
                          )}
                    </DataTag>
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.createdAt')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue
                      value={formatTimestamp(user.createdAt, utcSuffix)}
                      hasCopy={false}
                      emptyText={dash}
                    />
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.lastLogin')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue
                      value={formatTimestamp(user.lastLogin, utcSuffix)}
                      hasCopy={false}
                      emptyText={String(
                        t('accountManagement.account.neverLoggedIn'),
                      )}
                    />
                  </DataList.ItemValue>
                </DataList.Item>

                <DataList.Item>
                  <DataList.ItemLabel>
                    {t('accountManagement.account.loginsCount')}
                  </DataList.ItemLabel>
                  <DataList.ItemValue>
                    <CopyableValue
                      value={user.loginsCount?.toString()}
                      hasCopy={false}
                      emptyText={dash}
                    />
                  </DataList.ItemValue>
                </DataList.Item>
              </DataList.Root>
            </VStack>
          </SectionCard>
        </GridItem>

        <GridItem>
          <SectionCard
            title={t('accountManagement.organizations.title')}
            width="100%"
            height="100%"
          >
            {/*
             * License-capable viewers (ADMIN or GTM) see enriched per-org
             * cards with decision signals (owner/member, cluster count,
             * license count) so they can disambiguate before opening Org
             * Details. Non-capable viewers keep the existing two-column
             * grid table below — zero regression for operators. See
             * openspec design.md Decision 3.
             */}
            {canManageLicenses && <OrgDisambiguationCards user={user} />}
            {!canManageLicenses && organizations.length === 0 && (
              <Text color="gray.500">
                {t('accountManagement.organizations.noOrganizations')}
              </Text>
            )}
            {!canManageLicenses && organizations.length > 0 && (
              <Table.Root>
                <Table.Body>
                  {organizations.map((org, index) => (
                    <React.Fragment key={org.orgId}>
                      {index > 0 && (
                        <Table.Row>
                          <Table.Cell colSpan={2}>
                            <Box borderTop="1px solid" borderColor="gray.200" />
                          </Table.Cell>
                        </Table.Row>
                      )}
                      <Table.Row>
                        <Table.Cell>
                          {t('accountManagement.organizations.orgId')}
                        </Table.Cell>
                        <Table.Cell>
                          <DataTag
                            fontWeight={500}
                            fontSize="1rem"
                            value={org.orgId}
                            hasCopy={true}
                            onClickBody={() =>
                              navigate({
                                to: hivesOverviewOrganizationDetailsRoute.to,
                                params: {
                                  orgId: org.orgId,
                                },
                              })
                            }
                          >
                            {org.orgId}
                          </DataTag>
                        </Table.Cell>
                      </Table.Row>
                      {org.orgName && (
                        <Table.Row>
                          <Table.Cell>
                            {t('accountManagement.organizations.orgName')}
                          </Table.Cell>
                          <Table.Cell>
                            <DataTag
                              fontWeight={500}
                              fontSize="1rem"
                              value={org.orgName}
                            >
                              {org.orgName}
                            </DataTag>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {org.ownerId && (
                        <Table.Row>
                          <Table.Cell>
                            {t('accountManagement.organizations.ownerId')}
                          </Table.Cell>
                          <Table.Cell>
                            <DataTag
                              fontWeight={500}
                              fontSize="1rem"
                              value={org.ownerId}
                            >
                              {org.ownerId}
                            </DataTag>
                          </Table.Cell>
                        </Table.Row>
                      )}
                      {org.ownerId && (
                        <Table.Row>
                          <Table.Cell>
                            {t('accountManagement.account.isOwner.label')}
                          </Table.Cell>
                          <Table.Cell>
                            <DataTag
                              fontWeight={500}
                              fontSize="1rem"
                              hasCopy={false}
                              colorPalette={
                                org.ownerId === user.id ? 'green' : 'red'
                              }
                              value={
                                org.ownerId === user.id
                                  ? String(
                                      t(
                                        'accountManagement.account.isOwner.yes',
                                      ),
                                    )
                                  : String(
                                      t('accountManagement.account.isOwner.no'),
                                    )
                              }
                            >
                              {org.ownerId === user.id
                                ? t('accountManagement.account.isOwner.yes')
                                : t('accountManagement.account.isOwner.no')}
                            </DataTag>
                          </Table.Cell>
                        </Table.Row>
                      )}
                    </React.Fragment>
                  ))}
                </Table.Body>
              </Table.Root>
            )}
          </SectionCard>
        </GridItem>
      </Grid>
    </Box>
  )
}

/**
 * Maps an Auth0 connection name to a friendly label, falling back to the raw
 * connection so an unmapped one stays readable and honest.
 */
function getConnectionDisplayValue(
  connection: string,
  t: (key: string) => string,
): string {
  const labelKey = CONNECTION_LABEL_KEYS[connection]
  return labelKey
    ? t(`accountManagement.account.authMethod.${labelKey}`)
    : connection
}

/**
 * Returns `undefined` for an absent timestamp so the caller's `emptyText`
 * decides how nothing reads — a dash for "never created" is wrong, "Never" for
 * a login that never happened is right.
 */
function formatTimestamp(
  timestamp: string | undefined,
  utcSuffix: string,
): string | undefined {
  if (!timestamp) {
    return undefined
  }

  const parsed = new Date(timestamp)
  if (Number.isNaN(parsed.getTime())) {
    return timestamp
  }

  return `${TIMESTAMP_FORMAT.format(parsed)} ${utcSuffix}`
}
